import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Loader2, FileText, Download, MailQuestion } from "lucide-react";

const DOC_LABELS = {
  statuts: "Statuts", kbis: "Extrait Kbis", rib: "RIB",
  piece_identite_dirigeant: "Pièce d'identité dirigeant",
  delegation_pouvoir: "Délégation pouvoir", autre: "Autre",
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const MOTIFS_REJET = {
  rejet_dossier_incomplet: "Dossier incomplet",
  rejet_incoherence: "Incohérences détectées",
  rejet_piece_expiree: "Pièce(s) expirée(s) ou non recevable(s)",
};
const MOTIFS_ACCEPT = {
  acceptation_conforme: "Dossier conforme",
  acceptation_reserves_levees: "Réserves levées",
};

export default function KycValidation({ entrepriseId, canValidate = false, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState(null); // 'verified' | 'rejected'
  const [notes, setNotes] = useState("");
  const [motifCode, setMotifCode] = useState("");
  const [predefinis, setPredefinis] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openPieces, setOpenPieces] = useState(false);
  const [messagePieces, setMessagePieces] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/entreprises/${entrepriseId}/kyc`);
      setData(data);
      if (!predefinis) {
        try { const r = await api.get("/kyc/messages-predefinis"); setPredefinis(r.data.messages); } catch { /* non admin */ }
      }
    }
    catch { /* ok */ }
    finally { setLoading(false); }
  }, [entrepriseId, predefinis]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const applyMotif = (code) => {
    setMotifCode(code);
    if (code && predefinis?.[code]) setNotes(predefinis[code]);
  };

  const validate = async () => {
    setSubmitting(true);
    try {
      await api.post(`/entreprises/${entrepriseId}/kyc/validate`, { status: action, notes: notes || null });
      toast.success(action === "verified" ? "KYC validé — email envoyé au dirigeant" : "KYC rejeté — email envoyé au dirigeant");
      setOpen(false); setAction(null); setNotes(""); setMotifCode("");
      onUpdated?.();
    } catch (err) { toast.error("Validation impossible"); }
    finally { setSubmitting(false); }
  };

  const demanderPieces = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/entreprises/${entrepriseId}/kyc/demande-pieces`, { message: messagePieces || null });
      toast.success(`Demande envoyée à l'entreprise (${r.data.email_status === "sent" ? "email" : "notification"}${r.data.email_status === "sent" ? "" : " — voir statut"})`);
      setOpenPieces(false); setMessagePieces("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Envoi impossible"); }
    finally { setSubmitting(false); }
  };

  const downloadDoc = async (docId) => {
    try {
      const token = localStorage.getItem("oscopge_token");
      const res = await fetch(`${BASE_URL}/api/entreprises/${entrepriseId}/kyc/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <>
      <Button
        variant="outline" size="sm"
        onClick={() => setOpen(true)}
        data-testid={`kyc-review-${entrepriseId}`}
        className="w-full mt-3 border-[#C5A059]/30 text-[#A88846] hover:bg-[#FDF9F1]"
      >
        <FileText className="h-3.5 w-3.5 mr-1" /> Dossier KYC
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kyc-dialog">
          <DialogHeader><DialogTitle>Dossier KYC</DialogTitle></DialogHeader>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#C5A059]" /></div>
          ) : !data ? (
            <p className="text-sm text-slate-500">Dossier non chargé.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase text-slate-500">Entreprise</div>
                <div className="font-semibold">{data.raison_sociale} — SIRET {data.siret}</div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Statut :</span>
                <StatusPill status={data.status} />
                {data.complete && <span className="text-xs text-emerald-600">· Dossier complet</span>}
                {data.missing_documents?.length > 0 && (
                  <span className="text-xs text-amber-600">· {data.missing_documents.length} pièce(s) manquante(s)</span>
                )}
              </div>

              <div>
                <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Documents déposés ({data.documents?.length || 0})</div>
                {(!data.documents || data.documents.length === 0) ? (
                  <p className="text-sm text-slate-400 italic">Aucun document.</p>
                ) : (
                  <div className="space-y-1">
                    {data.documents.map((d) => (
                      <div key={d.id} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="font-medium">{DOC_LABELS[d.doc_type] || d.doc_type}</div>
                          <div className="text-xs text-slate-500 truncate">{d.filename} · {(d.size / 1024).toFixed(0)} ko · {d.uploaded_at?.slice(0,10)}</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => downloadDoc(d.id)} data-testid={`kyc-doc-dl-${d.id}`}><Download className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>
                )}
                {data.missing_documents?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Manquants : {data.missing_documents.map(m => DOC_LABELS[m]).join(", ")}
                  </div>
                )}
              </div>

              {data.notes && (
                <div className="text-sm bg-slate-50 border border-slate-200 rounded p-3">
                  <div className="text-xs uppercase text-slate-500 mb-1">Notes gestionnaire</div>
                  {data.notes}
                </div>
              )}

              {canValidate && data.missing_documents?.length > 0 && data.status !== "verified" && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => {
                    const pieces = data.missing_documents.map(m => DOC_LABELS[m]).join(", ");
                    setMessagePieces((predefinis?.pieces_manquantes || "").replace("{pieces}", pieces));
                    setOpenPieces(true);
                  }}
                  data-testid="kyc-demande-pieces-btn"
                  className="w-full border-amber-300 text-amber-800 hover:bg-amber-50"
                >
                  <MailQuestion className="h-3.5 w-3.5 mr-1" /> Demander les pièces manquantes à l'entreprise
                </Button>
              )}

              {canValidate && (
                <div className="pt-3 border-t border-slate-200">
                  {!action ? (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setAction("verified")}
                        data-testid="kyc-approve-btn"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approuver
                      </Button>
                      <Button
                        onClick={() => setAction("rejected")}
                        data-testid="kyc-reject-btn"
                        variant="outline"
                        className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Rejeter
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium">Motif prédéfini (optionnel — personnalisable ci-dessous)</label>
                        <Select value={motifCode} onValueChange={applyMotif}>
                          <SelectTrigger data-testid="kyc-motif-select"><SelectValue placeholder="Choisir un motif prédéfini…" /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(action === "verified" ? MOTIFS_ACCEPT : MOTIFS_REJET).map(([code, lbl]) => (
                              <SelectItem key={code} value={code}>{lbl}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">
                          {action === "verified" ? "Note (optionnelle, envoyée par email)" : "Motif du rejet (envoyé par email) *"}
                        </label>
                        <Textarea
                          rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                          data-testid="kyc-notes-input"
                          placeholder={action === "verified" ? "Bienvenue dans O'SCOP GE !" : "Détaillez les pièces à corriger ou compléter…"}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => { setAction(null); setNotes(""); setMotifCode(""); }}>Annuler</Button>
                        <Button
                          onClick={validate}
                          disabled={submitting || (action === "rejected" && !notes.trim())}
                          data-testid="kyc-validate-submit"
                          className={action === "verified" ? "bg-emerald-600 hover:bg-emerald-700 text-white flex-1" : "bg-red-600 hover:bg-red-700 text-white flex-1"}
                        >
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                          Confirmer {action === "verified" ? "l'approbation" : "le rejet"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openPieces} onOpenChange={setOpenPieces}>
        <DialogContent data-testid="kyc-pieces-dialog" aria-describedby="kyc-pieces-desc">
          <DialogHeader>
            <DialogTitle>Demande de pièces manquantes</DialogTitle>
            <DialogDescription id="kyc-pieces-desc">Message prédéfini personnalisable, envoyé directement à l'entreprise.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea rows={8} value={messagePieces} onChange={(e) => setMessagePieces(e.target.value)} data-testid="kyc-pieces-message" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpenPieces(false)}>Annuler</Button>
              <Button onClick={demanderPieces} disabled={submitting || !messagePieces.trim()} data-testid="kyc-pieces-send" className="bg-[#1B2A4A] text-white">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MailQuestion className="h-4 w-4 mr-1" />}
                Envoyer à l'entreprise
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatusPill({ status }) {
  const cfg = {
    verified: { cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2, label: "Vérifié" },
    rejected: { cls: "bg-red-100 text-red-700 border-red-300", icon: XCircle, label: "Rejeté" },
    pending: { cls: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock, label: "En attente" },
  }[status] || { cls: "bg-slate-100 text-slate-700", icon: Clock, label: status };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${cfg.cls}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}
